import { css, Styles, withStyles } from '@recruit-robin/robin-theme/web-styles';

import { af, compactQuery } from '../theme';

export type IntakeJourneyTrackerStyleProps = {
  /** How many stops the tracker draws. Decides the column count and the rail's inset. */
  total: number;
};

/**
 * Half a column. Stop centres sit at (i + 0.5) / total, so the first and last centres are
 * this far in from either edge — which is exactly where the rail has to start and stop.
 */
const halfColumn = (total: number) => 100 / (total * 2);

/** The centre of stop `index`, as a percentage of the tracker's width. */
export const stopCentre = (index: number, total: number) => ((index + 0.5) / total) * 100;

const styles = {
  /**
   * The gap above the tracker is load-bearing, not breathing room: it is the sky the hop is
   * measured against, so shrinking it flattens the rating scale until one star and five look
   * the same. The design's total clearance from the body copy is 136px (80px compact), and the
   * card is a flex column so margins do not collapse — this is that total less the body's own
   * 20px bottom margin. Change it and re-measure the five heights.
   */
  root: css`
    position: relative;
    margin-top: 116px;

    ${compactQuery} {
      margin-top: 60px;
    }
  `,

  /** The unwalked road: a hairline the trail is drawn over. */
  rail: ({ props: { total } }) => css`
    position: absolute;
    left: ${halfColumn(total)}%;
    right: ${halfColumn(total)}%;
    top: 3px;
    height: 2px;
    border-radius: ${af.rPill};
    background: ${af.lineSoft};
  `,

  /**
   * The travelled part. Only position and size live here — the dashes, the mask and the
   * flowing trim all come from `roadCss.fill`, so this trail and the progress bar's cannot
   * drift apart.
   */
  trail: ({ props: { total } }) => css`
    position: absolute;
    left: ${halfColumn(total)}%;
    top: 2px;
    height: 4px;
    /* Overrides roadCss.fill's own transition: this one moves on the advance curve. */
    transition: width 0.7s cubic-bezier(0.34, 1.26, 0.48, 1);
  `,

  /**
   * One grid column per stop, each owning its own dot and label.
   *
   * This is the whole reason the tracker is a grid: dot and label are in the same box, so
   * they cannot drift apart at any width, and adding a stop is a change to `total` alone.
   */
  cols: ({ props: { total } }) => css`
    position: relative;
    display: grid;
    grid-template-columns: repeat(${total}, 1fr);
  `,

  col: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  `,

  dot: css`
    width: 6px;
    height: 6px;
    border-radius: 2px;
    background: ${af.line};
    display: block;
  `,

  dotDone: css`
    background: ${af.ink};
  `,

  label: css`
    text-align: center;
  `,

  /**
   * The scooter rides above the rail. `left` is written inline from the stop geometry;
   * `roadCss.rider` supplies the transition curve and `roadCss.vehicle` the engine jitter.
   *
   * `translateX(-50%)` centres it on the stop, and has to be a wrapper concern rather than
   * part of the rider css — the vehicle inside is already animating `transform`.
   */
  rider: css`
    position: absolute;
    top: -32px;
    bottom: auto;
    width: 34px;
    transform: translateX(-50%);
  `,

  vehicle: css`
    width: 34px;

    svg {
      width: 34px;
      height: auto;
    }
  `,
} satisfies Styles<IntakeJourneyTrackerStyleProps>;

export const useStyles = withStyles(styles);
